/**
 * `/login`. Someone already signed in goes straight in (in `beforeLoad`, so
 * it can't race the page's own success navigation). After signing in, the
 * page is replaced, so Back from the landing page doesn't return here.
 */
import type { JSX } from "react";
import {
  createFileRoute,
  defaultParseSearch,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { safeRedirectPath } from "@linkhr/auth";
import { z } from "zod";
import { LoginPage } from "../auth/LoginPage";

const searchSchema = z.object({
  redirect: z.string().optional(),
  reason: z.enum(["session_revoked", "password_changed", "workspace_closed"]).optional(),
});

/** Split a router-internal `path?query` the way the router itself parses it. */
function target(to: string, parse: (query: string) => Record<string, unknown>) {
  const qIdx = to.indexOf("?");
  const search = qIdx === -1 ? undefined : parse(to.slice(qIdx));
  return {
    to: qIdx === -1 ? to : to.slice(0, qIdx),
    ...(search !== undefined && Object.keys(search).length > 0 ? { search } : {}),
  };
}

export const Route = createFileRoute("/login")({
  validateSearch: searchSchema,
  beforeLoad: ({ context, search }) => {
    if (!context.auth.getSnapshot().isAuthenticated) return;
    throw redirect({
      ...target(safeRedirectPath(search.redirect), defaultParseSearch),
      replace: true,
    });
  },
  component: LoginRouteComponent,
});

function LoginRouteComponent(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  return (
    <LoginPage
      redirect={search.redirect}
      reason={search.reason}
      onSuccess={(to) => {
        void navigate({ ...target(to, router.options.parseSearch), replace: true });
      }}
    />
  );
}
